import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import type { TelegramAuthPayload } from '../../utils/telegramAuth';

type TelegramLoginButtonProps = {
  botUsername?: string;
  onAuth: (payload: TelegramAuthPayload) => void;
  size?: 'large' | 'medium' | 'small';
  cornerRadius?: number;
  showUserPic?: boolean;
  label?: string;
  hideLabel?: boolean;
};

const PlaceholderButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: not-allowed;
`;

const ButtonShell = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  z-index: 2;
`;

const VisualButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  height: 28px;
  min-height: 28px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
  pointer-events: none;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const VisualIcon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  object-fit: contain;
`;

const WidgetOverlay = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  opacity: 0.01;
  overflow: hidden;
  cursor: pointer;
  pointer-events: auto;
  background: transparent;
`;

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramAuthPayload) => void;
  }
}

const TelegramLoginButton: React.FC<TelegramLoginButtonProps> = ({
  botUsername,
  onAuth,
  size = 'large',
  cornerRadius = 10,
  showUserPic = true,
  label = 'Connect',
  hideLabel = false
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!botUsername || !containerRef.current) return;

    window.onTelegramAuth = onAuth;

    const container = containerRef.current;
    container.innerHTML = '';

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', size);
    script.setAttribute('data-userpic', showUserPic ? 'true' : 'false');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-radius', String(cornerRadius));

    container.appendChild(script);

    return () => {
      if (window.onTelegramAuth === onAuth) {
        delete window.onTelegramAuth;
      }
    };
  }, [botUsername, onAuth, size, cornerRadius, showUserPic]);

  if (!botUsername) {
    return <PlaceholderButton>Telegram login disabled</PlaceholderButton>;
  }

  return (
    <ButtonShell>
      <VisualButton>
        <VisualIcon
          src="https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/TG.png"
          alt="Telegram"
        />
        {!hideLabel && label}
      </VisualButton>
      <WidgetOverlay ref={containerRef} />
    </ButtonShell>
  );
};

export default TelegramLoginButton;
